// approver-onboarding-progress-button/index.tsx
import React, { useEffect, useState } from 'react'
import { Button } from 'antd'
import { PlayCircleOutlined } from '@ant-design/icons'
import { App } from 'antd'
import { useManualQuery } from 'graphql-hooks'
import { IUserInfo } from '../../types/users'
import { ILocale } from '@vacationtracker/shared/types/i18n'
import {
  getApproverOnboardingProgress,
  shouldShowApproverOnboardingProgress,
  initializeApproverOnboardingProgress,
  ApproverOnboardingProgress,
  APPROVER_ONBOARDING_PROGRESS_EVENT
} from '../../util/approver-onboarding-storage'
import { showApproverOnboardingModal } from '../approver-onboarding-modal'
import { getLocationsApproverTeamsAndLabels } from '../../graphql/custom-queries'
import { IGetLocationsApproverTeamsAndLabelsShort } from '../../types/custom-queries'
import IntlMessages from '../../util/IntlMessages'

interface ApproverOnboardingProgressButtonProps {
  authUser: IUserInfo
  locale: ILocale
  companyId: string
  companyPlan?: string
}

export const ApproverOnboardingProgressButton: React.FC<ApproverOnboardingProgressButtonProps> = ({
  authUser,
  locale,
  companyId,
  companyPlan,
}) => {
  const { modal } = App.useApp()
  const [progress, setProgress] = useState<ApproverOnboardingProgress | null>(null)
  const [shouldShow, setShouldShow] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Query to fetch fresh department data
  const [getApproverTeamsQuery] = useManualQuery<IGetLocationsApproverTeamsAndLabelsShort, { id: string }>(
    getLocationsApproverTeamsAndLabels
  )

  // Calculate progress display (welcome step doesn't count, so subtract 1)
  const currentStep = Math.max(1, (progress?.currentStep || 0)) // Convert 0-based to 1-based, min 1
  const totalSteps = 3

  useEffect(() => {
    if (authUser?.id && companyId) {
      const isUserApprover = (authUser.approverToTeams?.length || 0) > 0
      const storedProgress = getApproverOnboardingProgress(authUser.id, companyId)
      setProgress(storedProgress)
      setShouldShow(shouldShowApproverOnboardingProgress(storedProgress, isUserApprover, companyPlan))

      // React to local progress updates
      const handleProgressUpdated = (event: CustomEvent<ApproverOnboardingProgress>) => {
        const updated = event.detail
        if (updated && updated.userId === authUser.id && updated.companyId === companyId) {
          const stillApprover = (authUser.approverToTeams?.length || 0) > 0
          setProgress(updated)
          setShouldShow(shouldShowApproverOnboardingProgress(updated, stillApprover, companyPlan))
        }
      }

      window.addEventListener(APPROVER_ONBOARDING_PROGRESS_EVENT, handleProgressUpdated as EventListener)
      return () => window.removeEventListener(APPROVER_ONBOARDING_PROGRESS_EVENT, handleProgressUpdated as EventListener)
    }
  }, [authUser?.id, authUser.approverToTeams, companyId, companyPlan])

  const handleClick = async () => {
    if (authUser && locale) {
      setIsLoading(true)

      try {
        // Fetch fresh department data to ensure we have the latest names
        const response = await getApproverTeamsQuery({ variables: { id: authUser.id } })

        let departmentNames: string[] = []

        // Note: Type definition IGetLocationsApproverTeamsAndLabelsShort incorrectly has getUser as array
        // but the actual GraphQL response returns a single object, so we cast to access correctly
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const responseData = response.data as any
        const approverTeams = responseData?.getUser?.approverToTeams

        if (approverTeams && Array.isArray(approverTeams) && approverTeams.length > 0) {
          // Use fresh data from the query
          departmentNames = approverTeams
            .map((team: { id: string; name?: string }) => team.name)
            .filter((name: unknown): name is string => Boolean(name && typeof name === 'string'))
        }

        // If query didn't return names, try authUser data
        if (departmentNames.length === 0 && authUser.approverToTeams) {
          departmentNames = authUser.approverToTeams
            .map(team => team.name)
            .filter((name): name is string => Boolean(name && typeof name === 'string'))
        }

        const isUserApprover = (authUser.approverToTeams?.length || 0) > 0
        const primaryDepartmentName = departmentNames[0] || 'Your Department'

        // Initialize progress if it doesn't exist
        if (!progress) {
          initializeApproverOnboardingProgress(authUser.id, companyId, primaryDepartmentName)
        }

        showApproverOnboardingModal(modal, authUser, locale, departmentNames, {
          companyId,
          onClose: () => {
            // Refresh progress after modal closes
            const updatedProgress = getApproverOnboardingProgress(authUser.id, companyId)
            setProgress(updatedProgress)
            setShouldShow(shouldShowApproverOnboardingProgress(updatedProgress, isUserApprover, companyPlan))
          },
          onExploreFirst: () => {
            // When user chooses explore first, don't advance step - just dismiss modal
            // They can resume from current step later via progress button
            const updatedProgress = getApproverOnboardingProgress(authUser.id, companyId)
            setProgress(updatedProgress)
            setShouldShow(shouldShowApproverOnboardingProgress(updatedProgress, isUserApprover, companyPlan))
          },
          onMinimize: () => {
            // When user minimizes, just save current progress as-is
            const updatedProgress = getApproverOnboardingProgress(authUser.id, companyId)
            setProgress(updatedProgress)
            setShouldShow(shouldShowApproverOnboardingProgress(updatedProgress, isUserApprover, companyPlan))
          },
        })
      } catch (error) {
        // If query fails, use fallback data from authUser
        const departmentNames = authUser.approverToTeams?.map(team => team.name).filter((name): name is string => Boolean(name && typeof name === 'string')) || []

        const isUserApprover = (authUser.approverToTeams?.length || 0) > 0
        const primaryDepartmentName = departmentNames[0] || 'Your Department'

        if (!progress) {
          initializeApproverOnboardingProgress(authUser.id, companyId, primaryDepartmentName)
        }

        showApproverOnboardingModal(modal, authUser, locale, departmentNames, {
          companyId,
          onClose: () => {
            const updatedProgress = getApproverOnboardingProgress(authUser.id, companyId)
            setProgress(updatedProgress)
            setShouldShow(shouldShowApproverOnboardingProgress(updatedProgress, isUserApprover, companyPlan))
          },
          onExploreFirst: () => {
            const updatedProgress = getApproverOnboardingProgress(authUser.id, companyId)
            setProgress(updatedProgress)
            setShouldShow(shouldShowApproverOnboardingProgress(updatedProgress, isUserApprover, companyPlan))
          },
          onMinimize: () => {
            const updatedProgress = getApproverOnboardingProgress(authUser.id, companyId)
            setProgress(updatedProgress)
            setShouldShow(shouldShowApproverOnboardingProgress(updatedProgress, isUserApprover, companyPlan))
          },
        })
      } finally {
        setIsLoading(false)
      }
    }
  }

  if (!shouldShow) {
    return null
  }

  return (
    <Button
      type="primary"
      icon={<PlayCircleOutlined />}
      onClick={handleClick}
      size="small"
      style={{ color: 'white' }}
      loading={isLoading}
    >
      <IntlMessages id="approver.onboarding.progress.button" values={{ current: currentStep, total: totalSteps }} />
    </Button>
  )
}
